/**
 * Dev-mode table of CloudWatch alarms: name, whether it counts toward an outage or degradation
 * (category alarms only), current state, when it entered that state, and CloudWatch's reason
 * (which carries the datapoint and threshold that tripped it).
 */
import React from 'react';
import { Box, StatusIndicator, Table } from '@amzn/awsui-components-react-v3/polaris';
import I18n from '../../../setupI18n';
import { AlarmDetail, AlarmStateValue } from '../types';

function AlarmState({ state }: { state: AlarmStateValue }): JSX.Element {
  switch (state) {
    case 'OK':
      return <StatusIndicator type="success">{I18n.t('OK')}</StatusIndicator>;
    case 'ALARM':
      return <StatusIndicator type="error">{I18n.t('In alarm')}</StatusIndicator>;
    case 'MISSING':
      return <StatusIndicator type="warning">{I18n.t('Missing')}</StatusIndicator>;
    case 'INSUFFICIENT_DATA':
    default:
      return <StatusIndicator type="stopped">{I18n.t('Insufficient data')}</StatusIndicator>;
  }
}

export function AlarmDetailsTable({ alarms }: { alarms: AlarmDetail[] }): JSX.Element {
  const levelColumn = {
    id: 'level',
    header: I18n.t('Counts as'),
    cell: (alarm: AlarmDetail) => {
      if (alarm.level === 'outage') return I18n.t('Outage');
      if (alarm.level === 'degraded') return I18n.t('Degraded');
      return '-';
    },
  };
  return (
    <Table
      variant="embedded"
      wrapLines
      items={alarms}
      columnDefinitions={[
        { id: 'name', header: I18n.t('Alarm'), cell: alarm => alarm.name },
        ...(alarms.some(alarm => alarm.level) ? [levelColumn] : []),
        { id: 'state', header: I18n.t('State'), cell: alarm => <AlarmState state={alarm.state} /> },
        {
          id: 'since',
          header: I18n.t('Since'),
          cell: alarm => (alarm.since ? new Date(alarm.since).toLocaleString() : '-'),
        },
        { id: 'reason', header: I18n.t('Reason'), cell: alarm => alarm.reason },
      ]}
      empty={
        <Box textAlign="center" color="inherit">
          {I18n.t('No alarms')}
        </Box>
      }
    />
  );
}
